import { Grid } from '@mui/material';
import { Typography, useEventCallback } from '@mui/material';
import RequestMunicipalNumberForm from 'civil-status/components/municipal-number/request-municipal-number-form';
import TableMunicipalNumber from 'civil-status/components/municipal-number/table';
import { getNameRegister } from 'civil-status/components/shared/list-register';
import { RequestMunicipalNumberCrumbs } from 'civil-status/constants/breadcrumb';
import { validationSchemaRequestMunicipalNumber } from 'civil-status/constants/schema-request-municipal-number';
import {
  MunicipalNumberList,
  RequestMunicipalNumberVM,
} from 'civil-status/view-models/municipal-number';
import { Form, Formik, FormikHelpers } from 'formik';
import { DateTime } from 'luxon';
import { Fragment, ReactElement, useMemo, useState } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Breadcrumbs, { BreadcrumbItem } from 'shell/components/shared/breadcrumb';
import { PmiButton } from 'shell/components/shared/button';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { HttpResponse } from 'shell/models/http-response';
import { MunicipalNumberResponse } from 'shell/models/municipal-numer';

const RequestMunicipalNumberPage = (): ReactElement => {
  const params = useParams();
  const navigate = useNavigate();

  const defaultInitialValues: RequestMunicipalNumberVM = useMemo(() => {
    return {
      register: params.idCat || '',
      nameRegister: params.valueCat?.toUpperCase() || '',
      numberElement: '',
      year: DateTime.now().toFormat('yyyy'),
    };
  }, [params.valueCat, params.idCat]);

  const [tableProps, setTableProps] = useState<Record<string, number>>({
    page: 0,
    rowsPerPage: 10,
  });
  const [list, setList] = useState<Array<MunicipalNumberList>>([]);

  const onSave = useHttpClient(`${__ACT_API_URL__}/numerazione-comunale/richiesta`, {
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
    },
  });

  const onSubmit = useEventCallback(
    (values: RequestMunicipalNumberVM, formikHelpers: FormikHelpers<RequestMunicipalNumberVM>) => {
      const data = {
        registro: values?.register || undefined,
        anno: values?.year ? Number.parseInt(values?.year, 10) : undefined,
        numeroElementi: values?.numberElement
          ? Number.parseInt(values?.numberElement, 10)
          : undefined,
      };

      (
        onSave({
          data,
        }) as ResponsePromise<HttpResponse<Array<MunicipalNumberResponse>>>
      ).then((response) => {
        toast.success('Operazione eseguita con successo');

        const castedResponse = response?.data.payload || [];

        const copyList = list.slice();
        const resList: Array<MunicipalNumberList> = castedResponse.map((itemResponse) => {
          return {
            id: itemResponse.idRichiesta || undefined,
            date: itemResponse.dataRichiesta || '',
            idAnsc: itemResponse.idAnsc || undefined,
            number: itemResponse.numero || undefined,
            operator: itemResponse.operatore || '',
            register: getNameRegister(itemResponse.registro || ''),
            state: itemResponse.stato || '',
            year: itemResponse.anno || undefined,
          };
        });

        const newList = copyList.concat(resList);

        setList(newList);

        formikHelpers.resetForm();
      });
    }
  );

  const goToResearch = useEventCallback(() => {
    if (!(params.idCat || params.valueCat)) {
      return;
    }

    navigate(
      generatePath('/stato-civile/:valueCat/:idCat/ricerca-numero', {
        idCat: params.idCat,
        valueCat: params.valueCat,
      })
    );
  });

  const getListData = useEventCallback(
    (_values, _data, item?: MunicipalNumberList, state?: string) => {
      let newList = (list || []).slice();
      newList = newList.map((currentItem) => {
        if (currentItem.id === item?.id && currentItem.number === item?.number) {
          return {
            ...currentItem,
            state,
          };
        }

        return currentItem;
      });

      setList(newList);
    }
  );

  const itemsBreadcrubs: Array<BreadcrumbItem> = useMemo(() => {
    return RequestMunicipalNumberCrumbs(params.valueCat || '', params.idCat || '');
  }, [params.valueCat, params.idCat]);

  return (
    <Fragment>
      <Breadcrumbs items={itemsBreadcrubs} />
      <div className="pmi-pt-4 pmi-pb-3 pmi-d-flex pmi-align-items-center pmi-justify-content-between">
        <Typography
          color="secondary.main"
          style={{
            fontSize: '26px',
          }}
        >
          Gestione numerazione comunale - Richiesta
        </Typography>
      </div>
      <div className="circle" />
      <Formik
        initialValues={defaultInitialValues}
        validateOnMount
        validationSchema={validationSchemaRequestMunicipalNumber}
        onSubmit={onSubmit}
      >
        <Form>
          <RequestMunicipalNumberForm />
        </Form>
      </Formik>
      {list.length > 0 ? (
        <TableMunicipalNumber
          list={list}
          getListData={getListData}
          tableProps={tableProps}
          setTableProps={setTableProps}
          isRequest
        />
      ) : undefined}
      <Grid container spacing={2}>
        <Grid item xs={12} md={3} style={{ display: 'flex', alignItems: 'center' }}>
          <PmiButton variant="outlined" color="cdmBrandThird" fullWidth onClick={goToResearch}>
            Vai alla ricerca
          </PmiButton>
        </Grid>
      </Grid>
    </Fragment>
  );
};

RequestMunicipalNumberPage.displayName = 'Request Municipal Number Page component';

export default RequestMunicipalNumberPage;
